// Revenus panel for the Comptabilité tab — aggregates SumUp orders + donations.
import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";

const fmt = (n) => Number(n || 0).toFixed(2);

export default function RevenusPanel({ locale = "fr" }) {
  const isFr = locale === "fr";
  const [orders, setOrders] = useState([]);
  const [donations, setDonations] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [o, d] = await Promise.all([
        api.get("/admin/orders"),
        api.get("/admin/donations"),
      ]);
      setOrders(Array.isArray(o.data) ? o.data : o.data.items || []);
      setDonations(d.data.items || []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const aggregates = useMemo(() => {
    const paidOrders = orders.filter((o) => o.payment_status === "paid");
    const byKind = { membership: 0, event: 0, presence: 0 };
    paidOrders.forEach((o) => {
      const amt = parseFloat(o.amount || 0);
      if (byKind[o.kind] !== undefined) byKind[o.kind] += amt;
    });
    const donationsPaid = donations.filter((d) => d.payment_status === "paid");
    const donationsTotal = donationsPaid.reduce((s, d) => s + parseFloat(d.amount || 0), 0);
    const donationsPending = donations
      .filter((d) => d.payment_status !== "paid")
      .reduce((s, d) => s + parseFloat(d.amount || 0), 0);
    const total = byKind.membership + byKind.event + byKind.presence + donationsTotal;

    // Recent paid transactions (orders + donations merged, sorted by date desc)
    const recent = [
      ...paidOrders.map((o) => ({
        id: `o-${o.id || o.session_id}`,
        kind: o.kind,
        label: o.customer_name || o.name || (o.kind === "membership" ? "Adhésion" : o.kind === "event" ? "Billetterie" : "Fêtes 2027"),
        amount: parseFloat(o.amount || 0),
        date: o.paid_at || o.created_at || "",
      })),
      ...donationsPaid.map((d) => ({
        id: `d-${d.id}`,
        kind: "donation",
        label: d.donor_name || d.name || "Don",
        amount: parseFloat(d.amount || 0),
        date: d.paid_at || d.created_at || "",
      })),
    ].sort((a, b) => (b.date || "").localeCompare(a.date || "")).slice(0, 15);

    return {
      total,
      membership: byKind.membership,
      event: byKind.event,
      presence: byKind.presence,
      donations: donationsTotal,
      donationsPending,
      donationsCount: donationsPaid.length,
      ordersCount: paidOrders.length,
      recent,
    };
  }, [orders, donations]);

  const KIND_LABELS = {
    membership: isFr ? "Adhésion" : "Membership",
    event: isFr ? "Billetterie" : "Tickets",
    presence: isFr ? "Fêtes 2027" : "Fêtes 2027",
    donation: isFr ? "Don" : "Donation",
  };
  const KIND_COLORS = {
    membership: "#4f6b3a",
    event: "#c98a3a",
    presence: "#991b2b",
    donation: "#9a8a2b",
  };

  return (
    <div className="space-y-4" data-testid="revenus-panel">
      <div className="flex flex-wrap items-center gap-3"
           style={{ paddingTop: "1rem", borderTop: "1px dashed var(--border)" }}>
        <h3 className="font-display text-xl">{isFr ? "Revenus" : "Revenue"}</h3>
        <span className="text-xs" style={{ color: "var(--muted)" }}>
          {isFr ? "Encaissements SumUp confirmés" : "Confirmed SumUp revenue"}
        </span>
        {loading && <span className="text-xs" style={{ color: "var(--muted)" }}>· {isFr ? "Chargement…" : "Loading…"}</span>}
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <RevenueKpi label="TOTAL" value={fmt(aggregates.total)} color="var(--moss)" highlight testId="rev-kpi-total" />
        <RevenueKpi label={isFr ? "Mécénat" : "Patronage"} value={fmt(aggregates.donations)}
                    sub={`${aggregates.donationsCount} ${isFr ? "don(s)" : "donation(s)"}`}
                    color={KIND_COLORS.donation} testId="rev-kpi-mecenat" />
        <RevenueKpi label={isFr ? "Adhésions" : "Memberships"} value={fmt(aggregates.membership)}
                    color={KIND_COLORS.membership} testId="rev-kpi-adhesion" />
        <RevenueKpi label={isFr ? "Billetterie" : "Tickets"} value={fmt(aggregates.event)}
                    color={KIND_COLORS.event} testId="rev-kpi-billetterie" />
        <RevenueKpi label="Fêtes 2027" value={fmt(aggregates.presence)}
                    color={KIND_COLORS.presence} testId="rev-kpi-presence" />
      </div>

      {aggregates.donationsPending > 0 && (
        <div className="text-xs" style={{ color: "var(--muted)" }}>
          {isFr ? "Dons en attente (BOOKING_OPEN=false) : " : "Pending donations: "}
          <strong style={{ color: "var(--gold)" }}>{fmt(aggregates.donationsPending)} €</strong>
        </div>
      )}

      {/* Recent transactions */}
      <div className="admin-card p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr style={{ borderBottom: "1px solid var(--border)" }}>
              {["Date", isFr ? "Type" : "Type", isFr ? "Source" : "Source", isFr ? "Montant" : "Amount"].map((h, i) => (
                <th key={i} className="text-left p-2 font-caps text-[0.6rem]"
                    style={{ letterSpacing: "0.18em", color: "var(--muted)" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {aggregates.recent.map((r) => (
              <tr key={r.id} style={{ borderBottom: "1px solid var(--border)" }}
                  data-testid={`revenue-row-${r.id}`}>
                <td className="p-2 text-xs" style={{ color: "var(--ink-2)" }}>
                  {(r.date || "").slice(0, 10) || "—"}
                </td>
                <td className="p-2">
                  <span className="px-2 py-0.5 text-[0.65rem] font-caps"
                        style={{
                          backgroundColor: `${KIND_COLORS[r.kind] || "var(--muted)"}26`,
                          color: KIND_COLORS[r.kind] || "var(--ink-2)",
                          letterSpacing: "0.15em",
                        }}>
                    {KIND_LABELS[r.kind] || r.kind}
                  </span>
                </td>
                <td className="p-2 text-sm">{r.label}</td>
                <td className="p-2 text-sm font-mono" style={{ color: "var(--moss)" }}>
                  +{fmt(r.amount)} €
                </td>
              </tr>
            ))}
            {aggregates.recent.length === 0 && !loading && (
              <tr><td colSpan="4" className="p-6 text-center" style={{ color: "var(--muted)" }}>
                {isFr ? "Aucun encaissement pour l'instant." : "No revenue yet."}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="text-[0.7rem]" style={{ color: "var(--muted)" }}>
        {isFr
          ? "→ Vue détaillée dans l'onglet « Finance » (budget + dons en attente + lignes budgétaires)."
          : "→ Detailed view in the \"Finance\" tab (budget + pending donations + budget lines)."}
      </div>
    </div>
  );
}

function RevenueKpi({ label, value, sub, color, highlight, testId }) {
  return (
    <div className="admin-card" data-testid={testId}
         style={highlight ? { borderColor: "var(--moss)", borderWidth: 2 } : undefined}>
      <div className="font-caps text-[0.6rem]" style={{ color: "var(--muted)", letterSpacing: "0.18em" }}>
        {label}
      </div>
      <div className="font-display text-2xl" style={{ color }}>{value} €</div>
      {sub && <div className="text-[0.65rem] mt-1" style={{ color: "var(--muted)" }}>{sub}</div>}
    </div>
  );
}
